<template>
	<div
		class="guided-tutorials"
		:class="{ 'show-guide': showGuide, 'display-none': displayNone, 'show-backdrop': !isHidden && !isMinimize }"
	>
		<VideoPlayerContainer
			v-if="currentTutorial"
			:tutorial="currentTutorial"
			:chapters="chapters"
			:isPlaying="isPlaying"
			:isHidden="isHidden"
			:isMinimize="isMinimize"
			:isChapterStart="isChapterStart"
			:currentChapterIndex="currentChapterIndex"
			:isFlowBuilder="isFlowBuilder"
			@setIsPlaying="setIsPlaying"
			@setPausedFromVideo="setPausedFromVideo"
			@setCurrentChapterIndex="(v) => (currentChapterIndex = v)"
			@setIsMinimized="(v) => (isMinimize = v)"
			@setIsChapterStart="(v) => (isChapterStart = v)"
			@setIsHidden="(v) => (isHidden = v)"
			@chapterFinished="saveGuideState"
		/>
		<BottomPlayerBar
			v-if="currentTutorial"
			:isPlaying="isPlaying"
			:showHideButton="!isPlaying && isPausedFromVideoControllers && !isHidden"
			:isHidden="isHidden"
			:currentChapterIndex="currentChapterIndex"
			:dotArray="chapters"
			:tutorialSteps="currentTutorial.tutorial_steps"
			@setIsPlaying="setIsPlaying"
			@setIsHidden="(v) => (isHidden = v)"
			@onClose="close"
			@dotClicked="(v) => setChapterBeginning(v)"
			@restartVideo="setChapterBeginning(currentChapterIndex)"
			@displayChapterList="openGuidedTutorialsChapterListDialog()"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { isGuidedTutorialOn, guidedTutorials } from '@/store/modules/onboarding/getters';
import { fetchGuidedTutorials, setIsGuidedTutorialOn } from '@/store/modules/onboarding/actions';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import AnalyticsService from '@/utils/AnalyticsService';

import VideoPlayerContainer from './VideoPlayerContainer';
import BottomPlayerBar from './BottomPlayerBar';

export default {
	name: 'GuidedTutorials',
	components: {
		VideoPlayerContainer,
		BottomPlayerBar,
	},
	props: {
		isFlowBuilder: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ isGuidedTutorialOn, guidedTutorials }),
		...mapGetters(['user']),
	},
	methods: {
		...mapActionsShorthand({ setIsGuidedTutorialOn, fetchGuidedTutorials }),
		...mapActions(['saveUserMetadata']),
		close() {
			this.setPausedFromVideo(true);
			this.setIsGuidedTutorialOn(false);
			setTimeout(() => (this.isHidden = false), 1000);
			this.saveGuideState();
		},
		setIsPlaying(v) {
			this.isPlaying = v;
		},
		setPausedFromVideo(v) {
			this.isPausedFromVideoControllers = v;
			this.setIsPlaying(!v);
		},
		setChapterBeginning(v) {
			this.currentChapterIndex = v;
			this.isChapterStart = true;
		},
		async openGuidedTutorialsChapterListDialog() {
			if (!this.displayChapterList) {
				this.isMinimize = true;
				this.displayChapterList = true;
				await AppDialogManagerService.openGuidedTutorialsChapterListDialog({
					chapters: this.currentTutorial.tutorial_steps,
					title: this.currentTutorial.title,
					playChapterCallback: (index) => {
						this.isMinimize = false;
						this.isHidden = false;
						this.setChapterBeginning(index);
					},
				});
				this.displayChapterList = false;
			}
		},
		saveGuideState() {
			const { currentTutorialIndexMetadata, currentChapterIndexMetadata } = this.getIndexes();
			const { currentTutorialIndex, currentChapterIndex } = this;

			if (
				currentTutorialIndexMetadata < currentTutorialIndex ||
				(currentTutorialIndexMetadata <= currentTutorialIndex && currentChapterIndexMetadata < currentChapterIndex)
			) {
				this.saveUserMetadata({ guidedTutorials: { currentTutorialIndex, currentChapterIndex } });
				this.sendAnalytics(currentTutorialIndex, currentChapterIndex);
			}
		},
		sendAnalytics(currentTutorialIndex, currentChapterIndex) {
			AnalyticsService.sendEvent('Guided Tutorial chapter started', { currentTutorialIndex, currentChapterIndex });
		},
		getIndexes() {
			const { currentTutorialIndex, currentChapterIndex } = this.user.profile.metadata.guidedTutorials || {};
			return { currentTutorialIndexMetadata: currentTutorialIndex || 0, currentChapterIndexMetadata: currentChapterIndex || 0 };
		},
		setIsDisplaying(isDisplaying) {
			if (isDisplaying) {
				this.displayNone = false;
				setTimeout(() => (this.showGuide = true), 100);
			} else {
				this.showGuide = false;
				setTimeout(() => (this.displayNone = true), 1000);
			}
		},
	},
	watch: {
		isGuidedTutorialOn: {
			handler(newV) {
				this.setIsDisplaying(newV);
			},
		},
	},
	async mounted() {
		const { currentTutorialIndexMetadata, currentChapterIndexMetadata } = this.getIndexes();
		this.currentTutorialIndex = currentTutorialIndexMetadata || 0;
		this.currentChapterIndex = currentChapterIndexMetadata || 0;

		if (this.tutorials.length === 0) {
			await this.fetchGuidedTutorials();
		}
		this.tutorials = this.guidedTutorials;
		if (this.tutorials?.length) {
			this.currentTutorial = this.tutorials[this.currentTutorialIndex || 0];
			this.chapters = this.currentTutorial.tutorial_steps.map((ts) => {
				const timeArr = ts.start_time.split(':');
				return Number(timeArr[0]) * 60 + Number(timeArr[1]) + Number(timeArr[2]) / 100;
			});
		}
		this.setIsDisplaying(this.isGuidedTutorialOn);
	},
	data() {
		return {
			tutorials: [],
			currentTutorial: null,
			currentTutorialIndex: null,
			currentChapterIndex: null,
			chapters: null,
			displayNone: true,
			showGuide: true,
			isPlaying: false,
			isHidden: false,
			isMinimize: false,
			isPausedFromVideoControllers: false,
			isChapterStart: false,
			displayChapterList: false,
		};
	},
};
</script>

<style lang="scss" scoped>
.guided-tutorials {
	position: fixed;
	height: calc(100vh);
	width: 100vw;
	left: 0;
	z-index: $z-index-guided-tutorials;
	pointer-events: none;

	> * {
		pointer-events: all;
	}

	&:not(.show-guide) {
		top: $bottom-player-bar-height;
		opacity: 0;
		border: 0 solid #5b5be6;
		transition: top 0.5s, opacity 0.5s linear 0.1s, border 0.1s;
	}
	&.show-guide {
		top: 0;
		border: 5px solid #5b5be6;
		opacity: 1;
		transition: top 0.5s, opacity 0.5s linear 0.2s, border 0.7s linear 0.2s;

		&.show-backdrop {
			background: #80899cb0;
		}
	}
}
</style>
